import React from 'react';
import { Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { trackAnalyticsEvent } from '../../../services/analytics';
import OverviewSection from './OverviewSection';
import EventsBrowser from './EventsBrowser';
import EventCatalog from './EventCatalog';
import UsageSection from './UsageSection';
import CleanupSection from './CleanupSection';
import RealtimeSection from './RealtimeSection';
import DateRangePicker from './components/DateRangePicker';
import ActiveFilterChips from './components/ActiveFilterChips';
import AnalyticsInspector from './components/AnalyticsInspector';
import { InspectorProvider, useInspector } from './hooks/useInspector';
import { AnalyticsView, useAnalyticsFilters } from './hooks/useAnalyticsFilters';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
interface NavItem {
  id: AnalyticsView;
  label: string;
  icon: React.ReactNode;
  helper?: string;
}

function buildNavItems(t: (key: string) => string): NavItem[] {
  return [
    { id: 'realtime', label: t('analytics.nav.realtime'), icon: <Icon d={ICON_FALLBACKS.Bolt} size={20} />, helper: t('analytics.nav.realtimeHelper') },
    { id: 'overview', label: t('analytics.nav.overview'), icon: <Icon d={ICON_FALLBACKS.Dashboard} size={20} />, helper: t('analytics.nav.overviewHelper') },
    { id: 'events', label: t('analytics.nav.events'), icon: <Icon d={ICON_FALLBACKS.Timeline} size={20} />, helper: t('analytics.nav.eventsHelper') },
    { id: 'users', label: t('analytics.nav.users'), icon: <Icon name="users" size={20} />, helper: t('analytics.nav.usersHelper') },
    { id: 'catalog', label: t('analytics.nav.catalog'), icon: <Icon d={ICON_FALLBACKS.MenuBook} size={20} />, helper: t('analytics.nav.catalogHelper') },
    { id: 'cleanup', label: t('analytics.nav.cleanup'), icon: <Icon d={ICON_FALLBACKS.DeleteSweep} size={20} />, helper: t('analytics.nav.cleanupHelper') },
  ];
}

export default function AnalyticsDashboard() {
  return (
    <InspectorProvider>
      <DashboardInner />
    </InspectorProvider>
  );
}

function DashboardInner() {
  const { t } = useTranslation('admin');
  const { filters, setFilters } = useAnalyticsFilters();
  const inspector = useInspector();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const NAV_ITEMS = React.useMemo(() => buildNavItems(t), [t]);

  return (
    <Stack spacing={2}>
      {/* Top bar: date range + active filter chips */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <DateRangePicker />
          <ActiveFilterChips />
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          // `minmax(0, 1fr)` is required — bare `1fr` defaults to `minmax(auto, 1fr)`,
          // which lets wide children (e.g. the Events DataGrid) blow the column out
          // and push the whole page past the viewport.
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '220px minmax(0, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* Left rail */}
        <Box
          sx={{
            position: { md: 'sticky' },
            top: { md: 16 },
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            overflowX: { xs: 'auto', md: 'visible' },
          }}
        >
          <List dense disablePadding sx={{ width: '100%', display: { xs: 'flex', md: 'block' }, flexDirection: 'row' }}>
            {NAV_ITEMS.map((item) => {
              const selected = filters.view === item.id;
              return (
                <ListItemButton
                  key={item.id}
                  selected={selected}
                  onClick={() => {
                    if (filters.view !== item.id) {
                      trackAnalyticsEvent('admin_analytics_view_changed', {
                        view: item.id,
                        previous_view: filters.view,
                        timestamp: new Date().toISOString(),
                      });
                    }
                    setFilters({ view: item.id });
                  }}
                  sx={{
                    borderRadius: 1.5,
                    mb: { md: 0.5 },
                    mr: { xs: 0.5, md: 0 },
                    minWidth: { xs: 'auto', md: 'auto' },
                    px: { xs: 1, md: 2 },
                    py: { xs: 0.5, md: 0.75 },
                    flexShrink: 0,
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                      '&:hover': { backgroundColor: 'action.selected' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 28, md: 32 }, color: selected ? 'primary.main' : 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={!isNarrow ? item.helper : undefined}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: selected ? 700 : 500, noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
          {!isNarrow && <Divider sx={{ mt: 1.5 }} />}
        </Box>

        {/* Active section */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Typography variant="h6" fontWeight={700}>
              {NAV_ITEMS.find((n) => n.id === filters.view)?.label ?? t('analytics.nav.overview')}
            </Typography>
            {NAV_ITEMS.find((n) => n.id === filters.view)?.helper && (
              <Typography variant="caption" color="text.secondary">
                · {NAV_ITEMS.find((n) => n.id === filters.view)?.helper}
              </Typography>
            )}
          </Stack>

          {filters.view === 'realtime' && <RealtimeSection />}
          {filters.view === 'overview' && <OverviewSection />}
          {filters.view === 'events' && <EventsBrowser />}
          {filters.view === 'users' && <UsageSection />}
          {filters.view === 'catalog' && <EventCatalog />}
          {filters.view === 'cleanup' && <CleanupSection />}
        </Box>
      </Box>

      <AnalyticsInspector target={inspector.target} onClose={inspector.close} />
    </Stack>
  );
}
